// Home screen — recent Emails, recent Templates, quick-create CTAs. Per
// spec/build-plan.md P-024 row + P-032 row ("'Duplicate from previous
// Email' workflow on Home / Email list") + tech-arch.md §6.
//
// **Recent count = 8.** The build-plan row reads "5-10 most recent Emails";
// 8 is a defensible mid-point. The chapter sends ~1-2 Emails per month,
// so 8 covers ~4-8 months of recent work without overwhelming the Home
// screen. Same value used for Templates for consistency.
//
// **"+ New Email" opens the picker modal at P-032** (was direct
// `createAndOpenEmail` at P-024). The picker offers three paths:
// pick a template, duplicate a previous Email, or start blank. Per
// Banks's pre-flight Ask 2 (option (a)). The "+ New Template" CTA
// remains direct — Templates have no analogous "duplicate from
// previous" surface (the Template-list screen has its own Duplicate
// per-row action).
//
// **P-037.31 §D — rich rows + "View all →" links.** Pre-§D the Recent
// sections rendered a slim text row (just a link to the editor). Banks
// requested parity with the full-list screens — same Edit / Duplicate
// / Delete actions surfaced inline so staff doesn't have to navigate
// out to Manage. Plus a "View all →" link in each section header for
// discoverability of the full-list screens. Row component
// (`<RecordRow>`) shared with `email-list.tsx` + `template-list.tsx`
// (4 consumers total — 3rd-use threshold met).
//
// **Read-side via `useStore()`, write-side via factories.** Per D-082:
// the hook is the read-side, the singleton is the write-side.

import { useState } from 'preact/hooks'

import { sortByUpdatedDesc } from '../../lib/sort'
import {
  createAndOpenTemplate,
  duplicateEmail,
  duplicateTemplate,
} from '../../store/factories'
import type { Email, Template } from '../../store/schemas'
import { store } from '../../store/store'
import { secondaryButtonClass } from '../button-classes'
import { ConfirmModal } from '../confirm-modal'
import { EMAIL_NAME_PLACEHOLDER, TEMPLATE_NAME_PLACEHOLDER } from '../constants'
import { useDeleteConfirm } from '../hooks/use-delete-confirm'
import { useStore } from '../hooks/use-store'
import { NewEmailModal } from './new-email-modal'
import { RecordRow } from './record-row'

const RECENT_COUNT = 8

function handleDuplicateEmail(e: Email) {
  const dup = duplicateEmail(e)
  store.setState((s) => ({ ...s, emails: [...s.emails, dup] }))
}

function handleDeleteEmail(id: string) {
  store.setState((s) => ({
    ...s,
    emails: s.emails.filter((e) => e.id !== id),
    currentEmailId: s.currentEmailId === id ? null : s.currentEmailId,
  }))
}

function handleDuplicateTemplate(t: Template) {
  const dup = duplicateTemplate(t)
  store.setState((s) => ({ ...s, templates: [...s.templates, dup] }))
}

function handleDeleteTemplate(id: string) {
  store.setState((s) => ({
    ...s,
    templates: s.templates.filter((t) => t.id !== id),
    currentTemplateId: s.currentTemplateId === id ? null : s.currentTemplateId,
  }))
}

export function HomeScreen() {
  const { emails, templates } = useStore()
  const recentEmails = sortByUpdatedDesc(emails).slice(0, RECENT_COUNT)
  const recentTemplates = sortByUpdatedDesc(templates).slice(0, RECENT_COUNT)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Two independent delete-confirm modals — one per record type. Each
  // hook drives its own pending-state machine so an in-flight Email
  // delete doesn't block a Template delete (or vice versa).
  const emailDelete = useDeleteConfirm(emails, handleDeleteEmail)
  const templateDelete = useDeleteConfirm(templates, handleDeleteTemplate)

  return (
    <div class="space-y-8">
      <header class="flex items-baseline justify-between gap-4">
        <h2 class="m-0 text-lg font-semibold">Home</h2>
        <div class="flex gap-2">
          <a href="#/blocks" class={`inline-block ${secondaryButtonClass}`}>
            Block Gallery
          </a>
          <button type="button" onClick={createAndOpenTemplate} class={secondaryButtonClass}>
            New Template
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            class="rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep"
          >
            New Email
          </button>
        </div>
      </header>

      <section aria-labelledby="home-recent-emails">
        <div class="mb-3 flex items-baseline justify-between gap-4">
          <h3
            id="home-recent-emails"
            class="m-0 text-sm font-semibold uppercase tracking-wide text-gray-600"
          >
            Recent Emails
          </h3>
          <a href="#/emails" class="text-sm text-ypo-navy hover:underline">
            View all →
          </a>
        </div>
        {recentEmails.length === 0 ? (
          <p class="m-0 text-sm text-gray-600">
            No Emails yet. Click "New Email" to create your first.
          </p>
        ) : (
          <ul class="m-0 list-none space-y-2 p-0">
            {recentEmails.map((e) => {
              const templateLabel = e.templateName !== '' ? e.templateName : 'No template'
              const blockLabel = `${e.blocks.length} block${e.blocks.length === 1 ? '' : 's'}`
              return (
                <RecordRow
                  key={e.id}
                  name={e.name || EMAIL_NAME_PLACEHOLDER}
                  subtitle={`${templateLabel} · ${blockLabel}`}
                  editHref={`#/emails/${e.id}`}
                  onDuplicate={() => handleDuplicateEmail(e)}
                  onDelete={() => emailDelete.request(e.id)}
                />
              )
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby="home-recent-templates">
        <div class="mb-3 flex items-baseline justify-between gap-4">
          <h3
            id="home-recent-templates"
            class="m-0 text-sm font-semibold uppercase tracking-wide text-gray-600"
          >
            Recent Templates
          </h3>
          <a href="#/templates" class="text-sm text-ypo-navy hover:underline">
            View all →
          </a>
        </div>
        {recentTemplates.length === 0 ? (
          <p class="m-0 text-sm text-gray-600">
            No Templates yet. Click "New Template" to create your first.
          </p>
        ) : (
          <ul class="m-0 list-none space-y-2 p-0">
            {recentTemplates.map((t) => (
              <RecordRow
                key={t.id}
                name={t.name || TEMPLATE_NAME_PLACEHOLDER}
                subtitle={`${t.blocks.length} block${t.blocks.length === 1 ? '' : 's'}`}
                editHref={`#/templates/${t.id}`}
                onDuplicate={() => handleDuplicateTemplate(t)}
                onDelete={() => templateDelete.request(t.id)}
              />
            ))}
          </ul>
        )}
      </section>

      <ConfirmModal
        open={emailDelete.pending !== undefined}
        title="Delete this Email?"
        message={
          emailDelete.pending !== undefined
            ? `"${emailDelete.pending.name || EMAIL_NAME_PLACEHOLDER}" will be removed. This cannot be undone.`
            : ''
        }
        onConfirm={emailDelete.onConfirm}
        onCancel={emailDelete.onCancel}
      />

      <ConfirmModal
        open={templateDelete.pending !== undefined}
        title="Delete this Template?"
        message={
          templateDelete.pending !== undefined
            ? `"${templateDelete.pending.name || TEMPLATE_NAME_PLACEHOLDER}" will be removed. This cannot be undone.`
            : ''
        }
        onConfirm={templateDelete.onConfirm}
        onCancel={templateDelete.onCancel}
      />

      <NewEmailModal
        open={pickerOpen}
        templates={templates}
        emails={emails}
        onCancel={() => setPickerOpen(false)}
      />
    </div>
  )
}
